import React, { useEffect, useReducer, useRef } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
  MenuItem,
  IconButton,
} from '@material-ui/core'
import clsx from 'clsx'
import CloseIcon from '@material-ui/icons/Close'
import { useIntl, FormattedMessage } from 'react-intl'
import { makeStyles } from '@material-ui/core/styles'
import CreditCardIcon from '@material-ui/icons/CreditCard'
import PrimaryButton from 'app/components/buttons/PrimaryButton'
import CancelButton from 'app/components/buttons/CancelButton'
import ResponsiveDialogAction from 'app/components/dialog/ResponsiveDialogAction'
import { TextInput } from 'app/components/FormsyHelperComponent'
import Formsy from 'formsy-react'
import * as R from 'ramda'
import SelectFormsy from '@fuse/components/formsy/SelectFormsy'
import { useFormsy } from 'commons/form'
import KeyboardDatePickerFormsy from '@fuse/components/formsy/KeyboardDatePickerFormsy'
import { ProgressWithText } from 'app/components/progress/ProgressWithText'
import moment from 'moment'
import effects from 'app/effects/effects'
import MessageAlert from 'app/components/MessageAlert'
import { useDispatch } from 'react-redux'
import Promise from 'bluebird'
import Assets from 'app/configs/assetConfigs'
import MembershipCardAvatarFormsy from './MembershipCardAvatarFormsy'
import { isBase64 } from 'commons/website'
import assetConfigs from 'app/configs/assetConfigs'

const MAX_LENGTH_MEMBERSHIP_CARD_NUMBER = 20

const useStyle = makeStyles((theme) => ({
  dialog: {
    '& .MuiDialog-paper': {
      maxWidth: 760,
    },
  },
  titleSection: {
    backgroundColor: '#064e75',
  },
  textGray: {
    color: theme.palette.darkGrey.main,
  },
  veryLight: {
    color: theme.palette.grey.veryLight,
  },
  veryLightBackground: {
    backgroundColor: theme.palette.grey.veryLight,
  },
  secondary: {
    color: theme.palette.secondary.main,
  },
  image: {
    height: 180,
    width: 180,
  },
  paddingInput: {
    '& .MuiInput-formControl input': {
      paddingRight: '55px',
    },
  },
}))

const membershipCardStatuses = [
  { value: 'active', labelId: 'membershipCard.form.status.active' },
  { value: 'canceled', labelId: 'membershipCard.form.status.canceled' },
  { value: 'revoked', labelId: 'membershipCard.form.status.revoked' },
  { value: 'suspended', labelId: 'membershipCard.form.status.suspended' },
]

const formDataToPayload = (subscriberId, formData) => ({
  subscriberId,
  cardId: R.prop('id', formData),
  groupCardId: R.prop('groupCardId', formData),
  cardNumber: R.prop('membershipNumber', formData),
  status: R.prop('status', formData),
  expireDate: R.prop('expirationDate', formData),
})

const payloadToFormData = (payload) => ({
  id: R.prop('id', payload),
  groupCardId: R.prop('groupCardId', payload),
  membershipNumber: R.prop('cardNumber', payload),
  status: R.prop('status', payload),
  expirationDate: R.compose(
    R.unless(
      R.isNil,
      R.compose((m) => m.toDate(), moment),
    ),
    R.prop('expireDate'),
  )(payload),
  cardId: R.prop('id', payload),
})

// const cards = [
//   { id: 1, name: 'hello' },
//   { id: 2, name: 'world' },
// ];
// const fetchMembershipCardOptions = subscriberId =>
//   new Promise(resolve => {
//     setTimeout(() => resolve(cards), 5000);
//   });

const fetchMembershipCardOptions = (subscriberId) =>
  effects.apis
    .getMemberCardOptions(subscriberId)
    .then((response) => response.data)

// const saveSubcriberMembershipCard = (subscriberId, id, data) =>
//   new Promise((resolve, reject) => {
//     setTimeout(() => resolve(), 1000);
//   });

const saveSubcriberMembershipCard = (subscriberId, cardId, data) => {
  const fetcher = R.isNil(cardId)
    ? effects.apis.createSubscriberMemberCard
    : effects.apis.updateSubscriberMemberCard

  return fetcher({
    subscriberId,
    data: {
      ...formDataToPayload(subscriberId, data),
      cardId: cardId,
    },
  })
}

const saveAvatar = (subscriber, avatar) =>
  effects.apis.updateSubscriber({
    ...R.omit(['avatar'], subscriber.crmInfo),
    id: subscriber.id,
    crmAvatar: avatar,
    tags: R.compose(R.map(R.prop('id')), R.prop('tags'))(subscriber),
  })

const Actions = {
  SET_FORM_DATA_FROM_REMOTE: 'SET_FORM_DATA_FROM_REMOTE',
  SET_FORM_DATA: 'SET_FORM_DATA',
  SET_FORM_VALID: 'SET_FORM_VALID',
  SET_FORM_INVALID: 'SET_FORM_INVALID',
  SUBMITING: 'SUBMITING',
  SUBMIT_RESULT: 'SUBMIT_RESULT',
  UPDATE_EXPIRATION_DATE: 'UPDATE_EXPIRATION_DATE',
  FETCH_START: 'FETCH_START',
  FETCH_RESULT: 'FETCH_RESULT',
}

// When there's only just one card available for selection
// And it's a creating new card for subscriber
// It's better UX to auto select that one card for place admin
function autoSelectInitialCard(formData, cardOptions) {
  const firstCardOption = R.o(
    R.head,
    R.filter(R.propEq('hasCard', false))(cardOptions),
  )
  return R.when(
    R.always(
      !R.isNil(firstCardOption) && R.propSatisfies(R.isNil, 'id', formData),
    ),
    R.set(R.lensProp('groupCardId'), R.prop('id', firstCardOption)),
  )(formData)
}

const isExpiredBadgeShow = R.compose(
  R.ifElse(
    R.isNil,
    R.always(false),
    R.compose((expireDate) => expireDate.isBefore(moment()), moment),
  ),
  R.prop('value'),
)

const formData = R.o(payloadToFormData, R.prop('formData'))

const reducer = (state, action) => {
  switch (action.type) {
    case Actions.SET_FORM_DATA_FROM_REMOTE:
      return {
        ...state,
        isFetching: false,
        initialData: autoSelectInitialCard(
          formData(action),
          R.prop('cardOptions', action),
        ),
        cardOptions: R.prop('cardOptions', action),
      }

    case Actions.SET_FORM_DATA:
      return R.set(R.lensProp('initialData'), action.value, state)

    case Actions.SET_FORM_VALID:
      return R.set(R.lensProp('isFormValid'), true, state)

    case Actions.SET_FORM_INVALID:
      return R.set(R.lensProp('isFormValid'), false, state)

    case Actions.SUBMITING:
      return R.set(R.lensProp('isSubmitting'), true, state)

    case Actions.SUBMIT_RESULT:
      return R.set(R.lensProp('isSubmitting'), false, state)

    case Actions.UPDATE_EXPIRATION_DATE:
      return R.set(
        R.lensProp('isExpiredBadgeShow'),
        isExpiredBadgeShow(action),
        state,
      )

    case Actions.FETCH_START:
      return R.set(R.lensProp('isFetching'), true, state)

    case Actions.FETCH_RESULT:
      return R.set(R.lensProp('isFetching'), false, state)
    default:
      return undefined
  }
}

export default ({ subscriber, membershipCard, onClose, onSaved }) => {
  const classes = useStyle()
  const { formatMessage } = useIntl()

  const formRef = useRef()
  const { isPristine, onChange } = useFormsy(formRef)
  const [state, dispatch] = useReducer(reducer, {
    cardOptions: null,
    isFormValid: false,
    isSubmitting: false,
    isFetching: false,
    initialData: undefined,
  })
  const dispatchRedux = useDispatch()

  const handleClose = () => {
    onClose({ ...subscriber, refetch: moment() })
  }

  const isEditMode = () => R.has('id', membershipCard)

  useEffect(() => {
    dispatch({ type: Actions.FETCH_START })
    Promise.all([fetchMembershipCardOptions(subscriber.id)])
      .then(([cardOptions]) => {
        dispatch({
          type: Actions.SET_FORM_DATA_FROM_REMOTE,
          formData: { ...membershipCard, avatar: avatar(subscriber) },
          cardOptions,
        })
      })
      .catch((error) => {
        dispatchRedux(MessageAlert.showNetworkError(formatMessage)(error))
      })
      .finally(() => {
        dispatch({ type: Actions.FETCH_RESULT })
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscriber.id])

  function handleChange(formData) {
    onChange(formData)
    dispatch({
      type: Actions.UPDATE_EXPIRATION_DATE,
      value: formData.expirationDate,
    })
  }

  function handleSubmit(formData) {
    const membershipCardFormData = R.omit(['avatar'], formData)
    dispatch({ type: Actions.SUBMITING })
    saveSubcriberMembershipCard(
      subscriber.id,
      R.path(['initialData', 'id'], state),
      membershipCardFormData,
    )
      .then(() => {
        const avatar = R.prop('avatar', formData)
        if (
          avatar &&
          isBase64(avatar) &&
          avatar !== R.path(['initialData', 'avatar'], state)
        )
          return saveAvatar(subscriber, avatar)
        else return Promise.resolve()
      })
      .then(() => {
        dispatchRedux(
          MessageAlert.showSuccess({
            message: formatMessage({
              id: 'membershipCard.form.save.successfully',
            }),
          }),
        )
        dispatch({ type: Actions.SET_FORM_DATA, value: formData })
        onSaved()
        handleClose()
      })
      .catch((error) => {
        dispatchRedux(MessageAlert.showNetworkError(formatMessage)(error))
      })
      .finally(() => {
        dispatch({ type: Actions.SUBMIT_RESULT })
      })
  }

  function handleValid() {
    dispatch({ type: Actions.SET_FORM_VALID })
  }
  function handleInvalid() {
    dispatch({ type: Actions.SET_FORM_INVALID })
  }

  function displayName(subscriber) {
    const crmFirstName = R.path(['crmInfo', 'firstName'], subscriber)
    const crmLastName = R.path(['crmInfo', 'lastName'], subscriber)
    if (R.all(R.complement(R.isNil), [crmFirstName, crmLastName])) {
      return crmFirstName + ' ' + crmLastName
    }

    return R.compose(
      R.join(' '),
      R.filter(R.complement(R.isNil)),
    )([subscriber.firstName, subscriber.lastName])
  }

  function avatar(subscriber) {
    const crmAvatar = R.path(['crmInfo', 'avatar'], subscriber)
    const subscriberAvatar = R.path(['avatar'], subscriber)
    if (!R.isNil(crmAvatar)) {
      return crmAvatar
    } else if (!R.isNil(subscriberAvatar)) {
      return subscriberAvatar
    } else {
      return Assets.personSilhouetteImage
    }
  }

  function inactiveCardOption(name) {
    return formatMessage(
      {
        id: 'membershipCard.form.cardOptions.inactive',
      },
      { name: name },
    )
  }

  function noneCardOptions(cardOptions) {
    return R.anyPass([R.isEmpty, R.isNil])(cardOptions)
  }

  const cardAvailableToSelect = R.filter(R.propEq('hasCard', false))

  function isUnauthoried(state) {
    return !state.isFetching && noneCardOptions(state.cardOptions)
  }

  function isShowForm(state) {
    return !state.isFetching && !noneCardOptions(state.cardOptions)
  }

  return (
    <Dialog
      maxWidth="md"
      className={classes.dialog}
      fullWidth={true}
      open={true}
    >
      <DialogTitle
        disableTypography={true}
        className={clsx('p-24', classes.titleSection)}
      >
        <div className="flex justify-between">
          <div className="flex">
            <div className="relative w-96 h-96">
              <img
                className="absolute w-full h-full rounded-full"
                src={avatar(subscriber)}
                alt="avatarSubscriber"
              />
            </div>
            <div className="ml-32 mb-24 flex-1 flex items-start sm:items-center flex-col sm:flex-row">
              <div className="text-24 text-white font-bold">
                {displayName(subscriber)}
              </div>
              {state.isExpiredBadgeShow && (
                <div
                  className={clsx(
                    'mt-8 sm:mt-0 sm:ml-16 text-14 px-10 py-4 rounded-full',
                    classes.veryLightBackground,
                    classes.textGray,
                  )}
                >
                  <FormattedMessage id="membershipCard.form.status.expired" />
                </div>
              )}
            </div>
          </div>

          <div>
            <IconButton
              color="primary"
              className="p-2 cursor-pointer inline-flex"
              onClick={handleClose}
            >
              <CloseIcon className="text-white" />
            </IconButton>
          </div>
        </div>
      </DialogTitle>

      <Formsy
        onChange={handleChange}
        onValidSubmit={handleSubmit}
        onValid={handleValid}
        onInvalid={handleInvalid}
        ref={formRef}
      >
        <DialogContent className="p-0">
          <div className="p-24 flex items-center">
            <CreditCardIcon className={clsx('h-10 w-10', classes.textGray)} />
            <span className={clsx('ml-3 text-18 font-bold', classes.textGray)}>
              <FormattedMessage id="membershipCard.form.title" />
            </span>
          </div>
          <div className={clsx('border-t', classes.veryLight)}></div>

          {state.isFetching && (
            <div className="h-400 flex justify-center items-center">
              <div className="flex items-center">
                <CircularProgress className="mr-12 h-32 w-32" />
                <span className="font-simibold text-18">
                  <FormattedMessage id="membershipCard.form.loading" />
                </span>
              </div>
            </div>
          )}
          {isUnauthoried(state) && (
            <div className="h-400 flex justify-center items-center">
              <div className="flex flex-col justify-center items-center">
                <img
                  className="h-216"
                  alt="No membership card available"
                  src={assetConfigs.membershipCardIcon.emptyMemberCard}
                />
                <span className="mt-32 font-simibold text-18">
                  <FormattedMessage id="membershipCard.form.noCardsAvailable" />
                </span>
              </div>
            </div>
          )}
          {isShowForm(state) && (
            <div className="p-12 sm:p-40">
              <div className="flex flex-col sm:flex-row">
                <div className="flex flex-col items-center sm:w-1/3">
                  <MembershipCardAvatarFormsy
                    name="avatar"
                    value={avatar(subscriber)}
                  />
                </div>
                <div className="sm:pl-12 sm:flex-auto">
                  <div className="flex flex-col items-center">
                    {!state.cardOptions && (
                      <div className="py-6 w-full max-w-360">
                        <ProgressWithText
                          label={
                            <FormattedMessage id="membershipCard.form.cardOptions.loading" />
                          }
                          isDisable
                        />
                      </div>
                    )}
                    {state.cardOptions && (
                      <SelectFormsy
                        className="w-full max-w-360"
                        name="groupCardId"
                        label={formatMessage({
                          id: 'membershipCard.form.cardName',
                        })}
                        value={R.propOr('', 'groupCardId', state.initialData)}
                        validations={{
                          isNotEmpty: ({ groupCardId }) => {
                            return !R.anyPass([R.isEmpty, R.isNil])(groupCardId)
                          },
                        }}
                        disabled={
                          isEditMode() ||
                          cardAvailableToSelect(state.cardOptions).length <= 0
                        }
                      >
                        {!isEditMode() ? (
                          R.map(
                            (card) =>
                              (!card.hasCard ||
                                card.id === membershipCard.groupCardId) && (
                                <MenuItem key={card.id} value={card.id}>
                                  {R.ifElse(
                                    R.propEq('status', 0),
                                    R.compose(
                                      inactiveCardOption,
                                      R.prop('name'),
                                    ),
                                    R.prop('name'),
                                  )(card)}
                                </MenuItem>
                              ),
                            state.cardOptions,
                          )
                        ) : (
                          <MenuItem
                            key={R.path(['groupCard', 'id'], membershipCard)}
                            value={R.path(['groupCard', 'id'], membershipCard)}
                          >
                            {R.path(['groupCard', 'name'], membershipCard)}
                          </MenuItem>
                        )}
                      </SelectFormsy>
                    )}

                    <TextInput
                      margin="dense"
                      containerClassName={clsx(
                        'mt-20 w-full max-w-360',
                        classes.paddingInput,
                      )}
                      labelId="membershipCard.form.membershipNumber"
                      placeholderId="membershipCard.form.membershipNumber"
                      variant="standard"
                      required
                      fullWidth
                      name="membershipNumber"
                      maxLength={MAX_LENGTH_MEMBERSHIP_CARD_NUMBER}
                      value={R.propOr(
                        '',
                        'membershipNumber',
                        state.initialData,
                      )}
                      validations={{
                        notEmpty: ({ membershipNumber }) =>
                          !R.isEmpty(membershipNumber),
                      }}
                    />

                    <SelectFormsy
                      name="status"
                      className="mt-20 w-full max-w-360"
                      label={formatMessage({
                        id: 'text.status',
                      })}
                      value={R.propOr(
                        R.prop('value', R.head(membershipCardStatuses)),
                        'status',
                        state.initialData,
                      )}
                    >
                      {R.map(
                        (option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {formatMessage({ id: option.labelId })}
                          </MenuItem>
                        ),
                        membershipCardStatuses,
                      )}
                    </SelectFormsy>

                    <KeyboardDatePickerFormsy
                      name="expirationDate"
                      className="mt-20 w-full max-w-360"
                      maxDate="01/01/2400"
                      minDate="01/01/1800"
                      variant="inline"
                      format="MM/dd/yyyy"
                      margin="dense"
                      label={formatMessage({
                        id: 'membershipCard.form.expirationDate',
                      })}
                      placeholder="MM/DD/YYYY"
                      value={R.propOr(
                        null,
                        'expirationDate',
                        state.initialData,
                      )}
                      required
                      KeyboardButtonProps={{
                        'aria-label': 'change date',
                      }}
                      invalidDateMessage={formatMessage({
                        id: 'membershipCard.form.expirationDate.invalidDateFormat',
                      })}
                      validations={{
                        validDateFormat: ({ expirationDate }) =>
                          !isNaN(expirationDate),
                      }}
                    />
                  </div>
                </div>
              </div>
              <div className="pt-20">
                <ResponsiveDialogAction
                  StartButton={
                    <CancelButton
                      onClick={handleClose}
                      className="w-full px-64 py-12"
                    />
                  }
                  EndButton={
                    <PrimaryButton
                      isLoading={state.isSubmitting}
                      labelId="action.save"
                      disabled={
                        !state.isFormValid || state.isSubmitting || isPristine
                      }
                      type="submit"
                      className="w-full px-64 py-12"
                    />
                  }
                />
              </div>
            </div>
          )}
        </DialogContent>
      </Formsy>
    </Dialog>
  )
}
